<template>
  <div
    v-if="!isMobileOrTablet && showNewDesktopPlpQuickFilters && isBooleanFilter"
  >
    <slot />
  </div>

  <!-- Dropdown Filter -->
  <div
    v-else-if="!isMobileOrTablet && showNewDesktopPlpQuickFilters"
    ref="dropdownRef"
    class="relative"
  >
    <Button
      class="flex items-center focus:ring-2"
      type="muted"
      :is-active="isActive || badge > 0"
      :data-test-id="`filter-dropdown-${filterSlug}`"
      @click="toggleDropdown"
    >
      <span>{{ translateLabel(label) }}</span>
      <span v-if="badge > 0">({{ badge }})</span>
      <IconArrowDown
        class="size-4 transition"
        :class="{ '-rotate-180': isFilterDropdownOpen }"
      />
    </Button>

    <!-- Dropdown Content -->
    <FocusTrap
      v-if="isFilterDropdownOpen"
      :active="isFilterDropdownOpen"
      :paused="!isFilterDropdownOpen"
    >
      <div
        v-show="isFilterDropdownOpen"
        ref="dropdownContentRef"
        class="absolute top-full z-50 mt-1 w-[400px] border border-black-40 bg-white p-4 shadow-lg"
        :class="{ 'right-0': isOverflowing, 'left-0': !isOverflowing }"
      >
        <div class="max-h-[250px] overflow-y-auto px-1 pb-1">
          <slot />
        </div>
        <div class="mt-4 flex justify-between border-t border-black-40 pt-3">
          <Button
            no-padding
            type="muted"
            class="px-2 py-1 text-base focus:ring-2"
            data-test-id="reset-filter-button"
            @click="handleReset"
          >
            {{ translateLabel(resetLabel) }}
          </Button>

          <Button
            no-padding
            ignore-capitalisation
            type="primary"
            class="px-2 py-1 text-base capitalize focus:ring-2"
            data-test-id="apply-filter-button"
            @click="handleApply"
          >
            {{ $t('filter.save') }}
          </Button>
        </div>
      </div>
    </FocusTrap>
  </div>

  <!-- Initial filter grouping -->
  <section v-else>
    <div
      class="flex items-center justify-between"
      :class="{
        'bg-black-10': isCollapsible && !isMobile,
        'border-t border-gold-40 bg-gray': isCollapsible && isMobile,
        'py-2': isMobile,
      }"
    >
      <slot name="label">
        <button
          v-if="isCollapsible"
          :id="`filter-${label}-control`"
          class="flex grow items-center px-4 py-2"
          :aria-expanded="!collapsed"
          :aria-controls="`filter-${label}-content`"
          @click="toggleSection"
        >
          <FilterGroupHeadline
            size="base"
            class="!leading-6"
            :badge="badge"
            :label="translateLabel(label)"
          />
          <Icon
            icon="arrow-down"
            class="ml-2 !h-3 !w-3"
            :class="!collapsed && 'rotate-180'"
          />
        </button>
        <FilterGroupHeadline
          v-else
          :id="`filter-${label}-control`"
          :badge="badge"
          :label="translateLabel(label)"
        />
      </slot>
      <slot name="action">
        <Button
          size="sm"
          type="ghost"
          class="px-4 py-0 text-xs"
          :class="{
            'opacity-0': !(badge > 0 || showAction),
            collapse: badge < 1,
          }"
          @click="$emit('click:reset')"
        >
          {{ translateLabel(resetLabel) }}
        </Button>
      </slot>
    </div>

    <div
      :id="`filter-${label}-content`"
      class="mt-2"
      role="region"
      :aria-labelledby="`filter-${label}-control`"
      :class="[
        height,
        {
          'mt-2': !isMobile,
          'm-4': isMobile && isCollapsible,
          hidden: collapsed,
        },
      ]"
    >
      <slot />
    </div>
  </section>
</template>

<script setup lang="ts">
import { onUnmounted, onMounted, watch, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PropType } from 'vue'
import { FocusTrap } from 'focus-trap-vue'
import FilterGroupHeadline from './FilterGroupHeadline.vue'
import { useDeviceType } from '~/composables/useDeviceType'
import useExperiments from '~/composables/useExperiments'
import Icon from '#layers/base/components/ui/icons/Icon.vue'

const props = defineProps({
  label: {
    type: String as PropType<string>,
    default: '',
  },
  resetLabel: {
    type: String as PropType<string>,
    default: 'reset',
  },
  badge: {
    type: Number as PropType<number>,
    default: 0,
  },
  showAction: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isCollapsible: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isBoolean: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  height: {
    type: [String, undefined] as PropType<string | undefined>,
    default: undefined,
  },
  isActive: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  filterSlug: {
    type: String as PropType<string>,
    default: '',
  },
})

const emit = defineEmits(['click:reset', 'click:apply', 'resetToInitialState'])

const i18n = useI18n()
const collapsed = ref(props.isCollapsible && props.badge < 1)
const isBooleanFilter = ref(props.isBoolean)
const isFilterDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobile, isMobileOrTablet } = useDeviceType()

const activeDropdown = ref<string | null>(null)
const dropdownContentRef = ref<HTMLElement | null>(null)
const isOverflowing = ref(false)

const checkOverflow = async () => {
  if (!dropdownRef.value) {
    return
  }

  const buttonRect = dropdownRef.value.getBoundingClientRect()
  const dropdownWidth = 400 // match dropdown w-[400px]
  const spaceRight = window.innerWidth - buttonRect.right
  const spaceLeft = buttonRect.left

  isOverflowing.value = spaceRight < dropdownWidth && spaceLeft >= dropdownWidth
}

watch(isFilterDropdownOpen, async (newVal) => {
  if (newVal) {
    await nextTick()
    await checkOverflow()
  }
})

const translateLabel = (label: string) => {
  const translationKey = `filter.${label.toLowerCase()}`
  const translatedText = i18n.t(translationKey)

  return translatedText === translationKey ? label : translatedText
}
const toggleSection = () => {
  collapsed.value = !collapsed.value
}

const toggleDropdown = async () => {
  if (activeDropdown.value === props.label) {
    emit('resetToInitialState')
    isFilterDropdownOpen.value = false
    activeDropdown.value = null
  } else {
    await checkOverflow()
    activeDropdown.value = props.label
    isFilterDropdownOpen.value = true
  }
}

const handleClickOutside = (event: MouseEvent) => {
  if (
    activeDropdown.value === props.label &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node)
  ) {
    emit('resetToInitialState')
    isFilterDropdownOpen.value = false
    activeDropdown.value = null
  }
}

const handleReset = () => {
  emit('click:reset')
  isFilterDropdownOpen.value = false
}

const handleApply = () => {
  emit('click:apply')
  isFilterDropdownOpen.value = false
}

const handleEscape = (event: KeyboardEvent) => {
  if (
    event.key === 'Escape' &&
    activeDropdown.value === props.label &&
    isFilterDropdownOpen.value
  ) {
    emit('resetToInitialState')
    isFilterDropdownOpen.value = false
    activeDropdown.value = null
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  window.addEventListener('resize', checkOverflow)
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  window.addEventListener('resize', checkOverflow)
  document.addEventListener('keydown', handleEscape)
})
</script>
